import { Alert, Group, Stack, Table, Text } from "@mantine/core";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";
import { SectionCard } from "../../components/ui";
import type { BillDetail } from "../../lib/api";
import { formatINR } from "../../lib/money";

/**
 * A generated bill, exactly as the server recorded it.
 *
 * Every figure here is a server value. Nothing is recalculated in the browser,
 * because the bill is the tax document: what the customer was charged is what
 * was written, not what a float in a React render agrees it should have been.
 */
export function BillView({ bill }: { bill: BillDetail }) {
  const intraState = bill.gstTreatment === "INTRA_STATE";
  const settled = Number(bill.outstandingAmount) <= 0;
  // Defaulted rather than assumed present: POST /bills once answered without
  // `payments`, and reading .length off it blanked the entire page. The server
  // now returns one shape for both, but a bill is a tax document and the
  // screen that shows it should degrade rather than disappear.
  const payments = bill.payments ?? [];
  const lineItems = bill.order?.lineItems ?? [];

  return (
    <>
      <SectionCard title="Invoice">
        <Stack gap={8} component="dl" m={0}>
          <Row label="Bill">{bill.reference}</Row>
          <Row label="Date">{new Date(bill.billDate).toLocaleDateString()}</Row>
          <Row label="Customer">{bill.customerName}</Row>
          <Row label="GSTIN">{bill.customerGstNumber ?? "Not registered"}</Row>
          <Row label="Place of supply">
            {bill.placeOfSupply} — {intraState ? "CGST + SGST" : "IGST"}
          </Row>
          {bill.order && (
            <Row label="Order">
              {bill.order.reference}
              {bill.order.preOrder ? ` · from ${bill.order.preOrder.reference}` : ""}
            </Row>
          )}
          {bill.generatedBy && (
            <Row label="Billed by">
              {bill.generatedBy.name} at{" "}
              {new Date(bill.generatedAt).toLocaleTimeString()}
            </Row>
          )}
        </Stack>
      </SectionCard>

      <SectionCard title="Lines" padding="md">
        <Stack gap="md">
          {/* Scrolls inside its own container rather than making the page
              scroll sideways, per the brief. Seven money columns genuinely
              need a table: they are read down, not across. */}
          <Table.ScrollContainer minWidth={620}>
            <Table verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Item</Table.Th>
                  <Table.Th ta="right">Qty</Table.Th>
                  <Table.Th ta="right">Rate</Table.Th>
                  <Table.Th ta="right">Disc.</Table.Th>
                  <Table.Th ta="right">Taxable</Table.Th>
                  <Table.Th ta="right">GST</Table.Th>
                  <Table.Th ta="right">Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lineItems.map((line) => (
                  <Table.Tr key={line.id}>
                    <Table.Td>
                      <Text fz="sm" c="gray.9">
                        {line.product.name}
                      </Text>
                      <Text fz="xs" c="gray.6" className="num">
                        {line.product.sku}
                        {line.product.hsnCode ? ` · HSN ${line.product.hsnCode}` : ""}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right" className="num">
                      {line.quantityFulfilled}
                    </Table.Td>
                    <Table.Td ta="right" className="num">
                      {formatINR(line.unitPrice)}
                    </Table.Td>
                    <Table.Td ta="right" className="num">
                      {Number(line.discountAmount) > 0
                        ? formatINR(line.discountAmount)
                        : "—"}
                    </Table.Td>
                    <Table.Td ta="right" className="num">
                      {formatINR(line.taxableValue)}
                    </Table.Td>
                    <Table.Td ta="right" className="num">
                      {Number(line.gstRate)}%
                      <Text fz="xs" c="gray.6" className="num">
                        {intraState
                          ? `${formatINR(line.cgstAmount)} + ${formatINR(line.sgstAmount)}`
                          : formatINR(line.igstAmount)}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right" className="num" fw={600}>
                      {formatINR(line.lineTotal)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          <Stack gap={6}>
            <Total label="Sub total" value={formatINR(bill.subTotal)} />
            <Total
              label="Discount"
              value={
                Number(bill.discountTotal) > 0
                  ? `− ${formatINR(bill.discountTotal)}`
                  : "—"
              }
            />
            <Total label="Taxable value" value={formatINR(bill.taxableValue)} />
            {intraState ? (
              <>
                <Total label="CGST" value={formatINR(bill.cgstAmount)} />
                <Total label="SGST" value={formatINR(bill.sgstAmount)} />
              </>
            ) : (
              <Total label="IGST" value={formatINR(bill.igstAmount)} />
            )}
            <Total label="Total" value={formatINR(bill.totalAmount)} strong />
          </Stack>
        </Stack>
      </SectionCard>

      <SectionCard title="Settlement">
        <Stack gap="sm">
          <Alert
            variant="light"
            color={settled ? "teal" : "yellow"}
            radius="md"
            p="sm"
            icon={
              settled ? (
                <IconCircleCheck size={18} stroke={1.8} />
              ) : (
                <IconAlertTriangle size={18} stroke={1.8} />
              )
            }
          >
            <Text fz="sm">
              {settled
                ? "Settled in full."
                : `${formatINR(bill.outstandingAmount)} still outstanding` +
                  (Number(bill.collectedAmount) > 0
                    ? ` — ${formatINR(bill.collectedAmount)} collected so far.`
                    : ".")}
            </Text>
          </Alert>
          {payments.map((payment) => (
            <Group key={payment.id} justify="space-between" gap="md" wrap="nowrap">
              <Text fz="sm" c="gray.9" className="num">
                {formatINR(payment.amount)} · {payment.paymentMethod}
              </Text>
              <Text fz="xs" c="gray.6">
                {new Date(payment.collectedAt).toLocaleString()}
              </Text>
            </Group>
          ))}
        </Stack>
      </SectionCard>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
      <Text component="dt" fz="sm" c="gray.6" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text component="dd" fz="sm" fw={500} c="gray.9" m={0} ta="right">
        {children}
      </Text>
    </Group>
  );
}

function Total({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <Group
      justify="space-between"
      gap="md"
      wrap="nowrap"
      pt={strong ? "xs" : 0}
      style={
        strong ? { borderTop: "1px solid var(--mantine-color-gray-3)" } : undefined
      }
    >
      <Text fz={strong ? "md" : "sm"} c={strong ? "gray.9" : "gray.6"}>
        {label}
      </Text>
      <Text
        fz={strong ? "lg" : "sm"}
        fw={strong ? 700 : 500}
        c="gray.9"
        className="num"
      >
        {value}
      </Text>
    </Group>
  );
}
